<script setup>

import BaseModal from '@/components/Alerts/BaseModal.vue'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import InputText from '@/components/Forms/InputText.vue'
import { computed, ref } from 'vue'
import { FileStatus } from 'filepond'
import { useAuthStore } from '@/stores/authStore.js'
import vueFilePond from 'vue-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { Toaster } from '@/models/utils/Toaster.js';
import { ApiGatewayResponse, HttpStatusCode } from '@/api/ApiGateway.js'
import MultiListBox from '@/components/Forms/MultiListBox.vue'
import UserLookupComboBox from '@/components/Forms/UserLookupComboBox.vue'
import { documentsApi } from '@/api/Documents/DocumentsApi.js'
import { VueTagsInput } from '@vojtechlanka/vue-tags-input'
import { isEmpty } from 'lodash'
import ToggleSwitch from '@/components/Forms/ToggleSwitch.vue'
import { UserpilotService } from '@/models/utils/UserpilotUtils.js'
import { echoes } from '@/composables/echo/useEchoes.js'

const props = defineProps({
	tagData: { type: Array, default: () => [] },
	modalError: { type: Object, default: () => ({ 'error_eh': false, 'error_msg': 'There was an error loading tags. Please try reloading the page.' }) },
})

const emit = defineEmits(['evCloseModal'])//Used by ManageDocumentsPage to close modal

const authStore = useAuthStore()
const toaster = new Toaster();

const
	document_name = ref(''),
	validationErrors = ref(null),
	selectedTags = ref([]),
	showDiscardChangesModal = ref(false),
	selectedAuthorizedUsers = ref([]);


//#region File Upload
const pond = ref(null)
const filesQueued = ref([])
const filesBeingProcessed = ref([])
const fileErrors = ref([])
const uploadProgress = ref(new Map())
const isUploading = ref(false)
const toggleBulkAdd = ref(false)
const unmatchedEmails = ref([])

//#region Email entry
const tag = ref('')
const tags = ref([])
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const validation = [{
	classes: 'email-only',
	rule: emailRegex,
	disableAdd: true
}]

const FilePond = vueFilePond(FilePondPluginFileValidateType)
const serverConfig = {
	url: import.meta.env.VITE_API_URL + '/documents/upload',
	process: (fieldName, file, metadata, load, p_error, progress, abort) => {
		//unused params after abort: transfer, options
		const formData = new FormData()

		const document = {
			"submitter_id": authStore.getAuth.user().id,
			"name": document_name.value,
			"organization_id": authStore.activeOrganizationId
		}
		formData.append("document", JSON.stringify(document))

		const tags = selectedTags.value.map(tag => tag.id)

		formData.append("tags", JSON.stringify(tags))

		formData.append(fieldName, file, file.name)
		formData.append('tenant_name', authStore.getAuth.tenantInfo().name)
		formData.append('organization_id', authStore.activeOrganizationId)
		formData.append('organization_name', authStore.getActiveOrganization.organization_name)

		const request = new XMLHttpRequest();
		request.open('POST', import.meta.env.VITE_API_URL + '/documents/upload')
		request.setRequestHeader('X-CSRF-TOKEN', window.csrf_token)
		request.setRequestHeader('Authorization', `Bearer ${useAuthStore().rawJwt}`)
		request.setRequestHeader('x-client-user-id', authStore.getAuth?.user()?.id)
		request.setRequestHeader('x-jwt-version-code', authStore.getAuth?.user()?.jwt_version_code)
		request.setRequestHeader('X-Socket-ID', echoes.socketId());

		request.upload.onprogress = (e) => {
			progress(e.lengthComputable, e.loaded, e.total)
		}

		request.onload = function () {
			if (request.status >= 200 && request.status < 300) {
				load(request.responseText)
			}
			else
			{
				const resp = JSON.parse(request.response)
				if (resp.errors && (resp.errors.length > 0)) {
					validationErrors.value = new ApiGatewayResponse({
						'status': HttpStatusCode.ValidationError,
						'response': {
							'data': {
								'errors': resp.errors
							}
						}
					}, JSON.parse(request.response))
				}


				p_error('File upload failed.')
			}
		}

		request.send(formData)

		return {
			abort: () => {
				request.abort()
				abort()
			},
		};
	},
}

const labelIdle = `
		<svg class="upload-icon mx-auto mb-2" width="31" height="29" viewBox="0 0 31 29" fill="none">
			<title>Upload</title>
			<desc>Upload files here</desc>
			<path d="M27.15 3.65H15.6L14.55 1.65C14 0.65 13 0 11.85 0H3.05C1.35 0 0 1.35 0 3.05V25.15C0 26.85 1.35 28.2 3.05 28.2H27.2C28.9 28.2 30.25 26.85 30.25 25.15V6.7C30.25 5 28.85 3.65 27.15 3.65ZM28 25.15C28 25.6 27.65 25.95 27.2 25.95H3.05C2.6 25.95 2.25 25.6 2.25 25.15V3.05C2.25 2.6 2.6 2.25 3.05 2.25H11.85C12.15 2.25 12.4 2.4 12.55 2.7L13.95 5.3C14.15 5.65 14.55 5.9 14.95 5.9H27.2C27.65 5.9 28 6.25 28 6.7V25.15Z" class="fill-current"/>
			<path d="M15.9008 10.2984C15.4508 9.84844 14.7508 9.84844 14.3008 10.2984L9.95078 14.5984C9.50078 15.0484 9.50078 15.7484 9.95078 16.1984C10.4008 16.6484 11.1008 16.6484 11.5508 16.1984L14.0008 13.7984V21.0984C14.0008 21.6984 14.5008 22.2484 15.1508 22.2484C15.8008 22.2484 16.2508 21.7484 16.2508 21.0984V13.7484L18.7508 16.1984C18.9508 16.3984 19.2508 16.4984 19.5508 16.4984C19.8508 16.4984 20.1508 16.3984 20.3508 16.1484C20.8008 15.6984 20.8008 14.9984 20.3508 14.5484L15.9008 10.2984Z" class="fill-current"/>
		</svg>
		Drag & Drop file here or<br/><p class='clickable-text'>Choose file</p>
`

const progressBar = computed(() => {
	let totalProgress = 0
	uploadProgress.value.forEach((progress) => {
		totalProgress += progress
	})
	totalProgress = Math.round(totalProgress * 100 / filesBeingProcessed.value.length)
	const prefix = totalProgress >= 100 ? 'Finishing up...' : 'Uploading...'
	totalProgress = `width: ${totalProgress}%;`

	return `${prefix}<p><div class="relative inset-0 justify-center items-center w-full mt-2"><div class="absolute rounded-full w-full h-2 border z-0"></div><div class="absolute rounded-full h-2 bg-primary z-10" style="${totalProgress}"></div></div>`
})

const uploadEnabled = computed(() => {
	return isUploading.value === false && filesQueued.value != null && filesQueued.value.length > 0
})

function onFileAdded(p_error, file) {
	const outerContainer = document.getElementById('filepond--item-' + file.id)
	let infoMain = outerContainer.querySelector('.filepond--file-info-main')

	if (infoMain) {
		const wrapper = document.createElement('div')
		wrapper.classList.add('flex', 'space-x-2', 'filepond--file-info-main', 'w-full')
		infoMain.parentNode.insertBefore(wrapper, infoMain)
		infoMain.classList.remove('filepond--file-info-main')
		wrapper.appendChild(infoMain)

		const spinnerId = 'file-exists-spinner-' + file.id
		const warningId = 'file-exists-warning-' + file.id
		const fileExistsWrapper = document.createElement('div')
		fileExistsWrapper.classList.add('truncate')
		fileExistsWrapper.innerHTML = `
			<svg id="` + spinnerId + `" role="img" aria-labelledby="spinnerTitle" class="pointer-events-auto animate-spin h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
				<title id="spinnerTitle">Checking for file name conflict...</title>
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
				<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
			</svg>
			<span id="` + warningId + `" class="hidden text-error pointer-events-auto" style="font-size: 14px;" title="A file with this name already exists. After uploading, please provide a description to avoid confusion.">A file with this name already exists. After uploading, please provide a description to avoid confusion.</span>
		`
		wrapper.appendChild(fileExistsWrapper)

		document.getElementById(spinnerId).classList.add("hidden")
	}
}

function onUpdateFiles(files) {
	filesBeingProcessed.value = []

	// Files about to begin processing
	filesBeingProcessed.value = []
	let filtered = files.filter((file) => {
		return file.status === FileStatus.PROCESSING_QUEUED
	})

	filtered.forEach((file) => {
		filesBeingProcessed.value.push(file)
	})

	// Files queued waiting for processing to start
	filesQueued.value = []
	filtered = files.filter((file) => {
		return file.status === FileStatus.IDLE
	})

	filtered.forEach((file) => {
		filesQueued.value.push(file)
	})

	isUploading.value = filesBeingProcessed.value.length > 0
}

function onFileProcessed(p_error, p_file)
{
	if (p_error) fileErrors.value.push({ "error": p_error, "file": { "name": p_file.filename, "status": p_file.status } })

	if (isProcessingComplete())
	{
		filesBeingProcessed.value = []
		uploadProgress.value = new Map()

		if (fileErrors.value.length === 0)
		{
			filesQueued.value = []

			if (selectedAuthorizedUsers.value) {
				const documentId = JSON.parse(p_file.serverId).record_data.id

				if (documentId) {
					const success = () => {
						isUploading.value = false
						dismissModal(true)
					}

					const failure = (error) => {
						isUploading.value = false
						validationErrors.value = error.showErrorBanner()
					}

					if (toggleBulkAdd.value) {
						documentsApi.addAuthorizedUsersByEmail(
							documentId,
							getEmails(),
							success,
							failure
						)
					}
					else {
						documentsApi.addAuthorizedUsersById(
							documentId,
							selectedAuthorizedUsers.value.map(user => user.id),
							success,
							failure
						)
					}
				}
			} else {
				isUploading.value = false
				dismissModal(true)
			}
		}
		else
		{
			isUploading.value = false
			toaster.error(errorDetails().join('. '))
		}
	}
}

/**
 * This is redundant in general, but in the case of a file that failed to upload retrying doesn't trigger onUpdateFiles()
 */
function onFileProcessStart(file) {
	const fileAlreadyThere = filesBeingProcessed.value.find((item) => {
		return item.id === file.id
	})

	if (!fileAlreadyThere) {
		fileErrors.value = []
		filesBeingProcessed.value.push(file)
	}
}

/**
 * Extracts the error details
 * @returns {string[]} Array of error strings.
 */
function errorDetails()
{
	const errors = []

	const rawErrors = validationErrors.value.error?.response?.data?.errors ?? []

	for (const i in rawErrors)
	{
		const error = rawErrors[i]
		if (Object.prototype.hasOwnProperty.call(error, 'detail'))
		{
			errors.push(error['detail'])
		}
	}
	if (errors.length <= 0)
	{
		errors.push('An unknown error was encountered.')
	}

	return errors
}

function onProcessFileProgress(file, progress) {
	uploadProgress.value.set(file.id, progress)
}

function onError()
{
	//unused params p_error, p_file, p_status
	isUploading.value = false
}

/**
 * If one or more of the files have an error when uploading, the onFilesProcessed() function does not get called and isUploading doesn't get updated to true
 */
function isProcessingComplete() {
	const nonErrorStatuses = [FileStatus.IDLE, FileStatus.LOADING, FileStatus.PROCESSING_QUEUED, FileStatus.PROCESSING, FileStatus.PROCESSING_COMPLETE]

	const filesProcessingOrComplete = filesBeingProcessed.value.filter(file => {
		return file.status in nonErrorStatuses
	})

	return filesProcessingOrComplete.length === 0
}

function beginUpload() {
	document.value = document_name.value.trim()
	fileErrors.value = []
	isUploading.value = true

	if (toggleBulkAdd.value) {
		documentsApi.validateUserEmails(getEmails(), (response) => {
			if (response.success) {
				unmatchedEmails.value = JSON.parse(response.unmatchedEmails)

				tags.value = tags.value.map((tag) => {
					return {
						...tag,
						classes: unmatchedEmails.value.includes(tag.text) ? 'ti-server-invalid' : 'ti-valid'
					}
				}).sort(function(a, b)
				{
					return b.classes.includes('ti-server-invalid') - a.classes.includes('ti-server-invalid')
				}).sort(function(a, b)
				{
					return b.classes.includes('ti-server-edited') - a.classes.includes('ti-server-edited')
				})

				if (unmatchedEmails.value.length > 0) {
					isUploading.value = false
				}
				else {
					pond.value.processFiles()
				}
			}
			else {
				toaster.error('Failed to validate Authorized User emails.')
			}
		})
	}
	else {
		pond.value.processFiles()
	}
}

//#endregion File Upload

/**
 * Checks if there are any changes made to the modal
 * @returns {boolean}
 */
function changesMade()
{
	return (document_name.value !== '' || selectedTags.value.length > 0 || filesQueued.value.length > 0 || (toggleBulkAdd.value && !isEmpty(tags.value)))
}

/**
 * Show or Hide the Discard Changes modal
 * @param {boolean} p_show_eh True to show, false to hide
 */
function toggleDiscardChangesModal(p_show_eh)
{
	showDiscardChangesModal.value = p_show_eh
}

/**
 * Check if there's any changes, if not, proceed with closing the modal
 * @param p_reload_eh True to reload the upload grid
 */
function dismissModal(p_reload_eh)
{
	if (!p_reload_eh && !props.modalError.error_eh && changesMade())
	{
		toggleDiscardChangesModal(true)
	}
	else
	{
		closeModal(p_reload_eh)
	}
}

/**
 * Do cleanup, show success toast and tell the UploadDocumentPage to close the modal
 * @param p_reload_eh
 */
function closeModal(p_reload_eh)
{
	if (p_reload_eh)
	{
		toaster.success("Your document has been successfully added.")

		UserpilotService.track('documents_add', {
			"user_id": authStore.getAuth.user().id,
			"organization_id": authStore.activeOrganizationId
		});
	}
	emit('evCloseModal', 'add', p_reload_eh)
}

function getEmails() {
	//This was throwing errors on load
	try
	{
		// It is possible the user clicks send before the email has been added to the tags list, so try to process it here
		const tagInput = document.getElementById('tag-input')
		const inputText = emailRegex.test(tagInput.value) ? tagInput.value : null

		const tagTexts = tags.value.map(tag => tag.text)

		if (inputText)
		{
			tagTexts.push(inputText)
		}

		return tagTexts
	}
	catch (e) { /* empty */ }

	return tags.value.map(tag => tag.text)
}

/**
 * Before saving the edited tag, check if it was one the server marked invalid.
 * If it was invalid, mark it as edited (changes bg color).
 * @param savedTag
 */
function beforeSavingTag(savedTag) {
	if (savedTag.tag.classes.includes('ti-server-invalid')) {
		savedTag.tag.classes = 'ti-server-edited'
		savedTag.saveTag()
	}
}

/**
 * Before deleting a tag, check to see if it was one the server marked invalid.
 * If it was invalid, remove it from the list of unmatchedEmails.
 * @param deletedTag
 */
function beforeDeletingTag(deletedTag) {
	const index = unmatchedEmails.value.indexOf(deletedTag.tag.text)

	if (index > -1) {
		unmatchedEmails.value.splice(index, 1)
	}

	deletedTag.deleteTag()
}

</script>

<template>
	<div>
		<BaseModal :show="true" border-classes="border-t-6 border-tertiary">
			<div class="w-136 h-192">
				<div class="flex justify-between border-b border-neutral w-full pb-6">
					<p class="flex-grow text-xl font-bold text-heading">Add a Document</p>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal(false)">
				</div>

				<div v-if="props.modalError.error_eh">
					<div class="w-130 mt-6 text-error font-bold">{{ props.modalError.error_msg }}</div>
				</div>

				<form>
					<div class="flex flex-col mt-5">
						<InputText label="Document name*" :required="true" type="text" placeholder="" input-height="h-12" autocomplete="off"
									  v-model="document_name" :errors="validationErrors" error-field="name" icon="edit" />

						<MultiListBox id="tags" v-model="selectedTags" title="Tags" placeholder="Select a tag"
										  label-class="font-medium text-label !mb-2" input-height="h-10" icon="edit" maxSearchResultsHeight="max-h-60"
										  :items="tagData" :show-options-on-top="false" />

						<div class="flex items-center">
							<span class="flex-grow">Authorized Users</span>
							<span class="mr-2">Bulk add</span>
							<ToggleSwitch screen_reader_text="Generic Toggle" v-model="toggleBulkAdd"></ToggleSwitch>
						</div>

						<UserLookupComboBox v-if="!toggleBulkAdd" id="authorized_users" v-model="selectedAuthorizedUsers" class="mt-2" title="" placeholder="Enter name or email"
						                    label-class="font-medium text-label !mb-2" input-height="h-10" icon="edit" maxSearchResultsHeight="max-h-60"
						                    endpoint="users/documents/userSearch"
						                    :endpoint_params="{ 'organization_id': authStore.activeOrganizationId }"
						                    :enterable="true" :show-options-on-top="false" />

						<div v-else class="flex mt-2 min-h-12.5 max-h-40 overflow-y-auto border border-neutral rounded hover:ring-1 hover:ring-primary-bold-hover hover:border-primary-bold-hover focus:ring-1 focus:ring-neutral-subtle focus:border-neutral-subtle">
							<vue-tags-input
								id="tag-input"
								class="w-full"
								placeholder="Paste a list of emails"
								v-model="tag"
								:tags="tags"
								:validation="validation"
								:delete-on-backspace="true"
								:add-on-key="[13, ' ', ',', ';']"
								:separators="[' ', ',', ';', '|', '\n']"
								:allow-edit-tags="true"
								@tags-changed="newTags => tags = newTags"
								@before-saving-tag="beforeSavingTag"
								@before-deleting-tag="beforeDeletingTag"
								autocomplete="off" />
						</div>

						<div v-if="unmatchedEmails.length > 0" class="text-sm text-hi-warning" :class="[{'-mt-3': !toggleBulkAdd}, {'mt-1': toggleBulkAdd}]">One or more of the emails entered does not match an existing user.</div>
						<div v-else class="text-sm text-grayscale-body" :class="[{'-mt-3': !toggleBulkAdd}, {'mt-1': toggleBulkAdd}]">Leave blank to share with the entire organization.</div>

						<label class="text-label mt-4 mb-2">Upload</label>
						<div class="border border-neutral rounded">
							<!--
								allow-process: false removes retry button if fails
								labelTapToRetry, labelTapToCancel: set to blank cause disabling retry doesn't hide it for some reason  -->
							<file-pond
								class="h-48"
								name="file"
								ref="pond"
								:label-idle="isUploading ? progressBar : labelIdle"
								:allow-multiple="false"
								:max-files="1"
								:server="serverConfig"
								:allow-process="false"
								labelTapToRetry=""
								labelTapToCancel=""
								:allow-revert="false"
								:allow-remove="true"
								:instant-upload="false"
								:max-parallel-uploads="1"
								@addfile="onFileAdded"
								@processfilestart="onFileProcessStart"
								@processfile="onFileProcessed"
								@updatefiles="onUpdateFiles"
								@processfileprogress="onProcessFileProgress"
								@error="onError"
							/>
						</div>
					</div>
					<!-- Save/Cancel -->
					<div class="flex w-full flex-col-reverse sm:flex-row mt-4">
						<div class="sm:mr-auto mt-1 sm:mt-0"></div><!-- remove if showing Deactivate -->
						<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="dismissModal(false)"
										  class="mt-3 sm:mt-0" :class="{'sm:mr-4': true}" />
						<SubmitButton label="Add" :size="ButtonSize.Small" @click.prevent="beginUpload" :disabled="!uploadEnabled" :loading="isUploading" />

					</div>
				</form>
			</div>
		</BaseModal>

		<Teleport to="html">
			<MultiButtonModal
				:show="showDiscardChangesModal"
				icon="info"
				title="Discard Changes"
				message="Are you sure?"
				subMessage="Leaving now will discard any changes made."
				:buttons="[
									new ModalButtonData(
										'Cancel', () => { toggleDiscardChangesModal(false) }, ButtonType.Outline, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { closeModal(false) }, ButtonType.Default, ButtonSize.Small
									)
								]"
			/>
		</Teleport>
	</div>
</template>

<style scoped>
/* deep needed for these styles to work with 'scoped' */
:deep(.filepond--panel)
{
	display:none !important;
}

:deep(.filepond--root .filepond--drop-label) {
	width: 100%;
	height: fit-content !important;
	transform: translate3d(0, 0, 0) !important;
	color: #0a0a0a;
	text-align: center;
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 1.5;
	padding: 3em 0em !important;

	label {
		width: 100% !important;
	}
}

:deep(.filepond--list) {
	.filepond--item {
		background-color: #EFF6FB !important;
		@apply border border-disabled rounded-md;
	}

	.filepond--file, .filepond--file-info-sub, .filepond--file-status-main, .filepond--file-status-sub {
		color: #11181D !important;
		@apply font-normal !important;
		opacity: 1;
	}

	.filepond--file-action-button:hover, .filepond--file-action-button:focus {
		@apply !ring-2 !ring-primary-darkest/65;
	}
}

:deep(.vue-tags-input) {
	@apply max-w-full min-h-full;

	.ti-input, .ti-focus .ti-input, .vue-tags-input.ti-focus .ti-input {
		@apply w-full min-h-full border-0;
		@apply text-body;
	}

	.ti-tag {
		@apply h-7;
	}

	.ti-content {
		@apply px-1;
	}

	.ti-tag.ti-server-invalid {
		@apply bg-hi-warning text-white !important;
	}

	.ti-tag.ti-server-edited {
		@apply bg-lo-warning-on-color text-black !important;
	}
}

</style>